import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotifierModule, NotifierService } from 'angular-notifier';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import { UsersService } from 'src/app/pages/administration/services/users.service';
import { ToastsContainer } from 'src/theme/component/toast/toast-container.component';

@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NotifierModule, NgxSpinnerModule, ToastsContainer],
  template: `
    <ngx-spinner bdColor="rgba(0, 0, 0, 0.8)" size="medium" color="#e5f175" type="square-jelly-box"
      [fullScreen]="true">
      <p style="color: #e5f175">Cargando...</p>
    </ngx-spinner>
    <app-toasts aria-live="polite" aria-atomic="true"></app-toasts>
    <notifier-container></notifier-container>

    <div class="modal-header password-modal-header">
      <div>
        <h4 class="modal-title">Cambiar contraseña</h4>
      </div>
      <button type="button" class="btn-close" aria-label="Close" (click)="close()"></button>
    </div>

    <div class="modal-body password-modal-body">
      <form [formGroup]="form" (ngSubmit)="save()">
        <div class="password-user-box mb-3">
          <span>Usuario</span>
          <strong>{{ userLabel }}</strong>
        </div>

        <div class="password-rules mb-3">
          <b>La nueva contraseña debe tener:</b>
          <ul>
            <li>Mínimo 8 caracteres</li>
            <li>Al menos una mayúscula y una minúscula</li>
            <li>Al menos un número</li>
            <li>Al menos un carácter especial</li>
          </ul>
        </div>

        <div class="mb-3">
          <h6 class="card-title">Nueva contraseña</h6>
          <input type="password" class="form-control" formControlName="password"
            [ngClass]="{ 'is-invalid': submitted && form.controls['password'].errors }" />
          <div *ngIf="submitted && form.controls['password'].errors" class="invalid-feedback">
            <div *ngIf="form.controls['password'].errors['required']">Contraseña es requerida</div>
            <div *ngIf="form.controls['password'].errors['minlength']">Debe tener al menos 8 caracteres</div>
            <div *ngIf="form.controls['password'].errors['pattern']">
              Debe contener al menos 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial.
            </div>
          </div>
        </div>

        <div class="mb-3">
          <h6 class="card-title">Confirmar contraseña</h6>
          <input type="password" class="form-control" formControlName="confirmPassword"
            [ngClass]="{
              'is-invalid': submitted && (form.controls['confirmPassword'].errors || form.errors?.['passwordMismatch'])
            }" />
          <div *ngIf="submitted && form.controls['confirmPassword'].errors" class="invalid-feedback">
            <div *ngIf="form.controls['confirmPassword'].errors['required']">Debe confirmar la contraseña</div>
          </div>
          <div *ngIf="submitted && form.errors?.['passwordMismatch']" class="invalid-feedback d-block">
            Las contraseñas no coinciden.
          </div>
        </div>

        <div [hidden]="!showTemporaryOption" class="form-check password-temporary-check mb-3">
          <input class="form-check-input" type="checkbox" id="temporarySharedPassword"
            formControlName="temporary" />
          <label class="form-check-label" for="temporarySharedPassword">
            Solicitar cambio al iniciar sesión
          </label>
          <small class="text-muted d-block">Si está activo, Keycloak pedirá cambiarla en el próximo ingreso.</small>
        </div>

        <div class="modal-footer px-0 pb-0">
          <button type="button" class="btn app-btn app-btn--secondary" (click)="close()">Cerrar</button>
          <button type="submit" class="btn app-btn app-btn--primary">Guardar contraseña</button>
        </div>
      </form>
    </div>
  `,
  styles: [`
    .password-modal-header {
      align-items: flex-start;
      border-bottom: 1px solid #e7eef5;
    }

    .password-modal-body {
      background: #f8fbfe;
    }

    .password-user-box,
    .password-rules {
      border: 1px solid #d9e6f3;
      border-radius: 8px;
      background: #ffffff;
      padding: 12px 14px;
    }

    .password-user-box span {
      display: block;
      color: #707070;
      font-size: 12px;
    }

    .password-user-box strong {
      color: #1c69b1;
    }

    .password-rules {
      color: #2f2f2f;
      font-size: 12px;
    }

    .password-rules ul {
      margin: 6px 0 0;
      padding-left: 18px;
    }

    .password-temporary-check {
      border-left: 3px solid #e66228;
      padding-left: 28px;
    }
  `]
})
export class ChangePasswordModalComponent implements OnInit {
  private currentUser: any;

  @Input() set user(value: any) {
    this.currentUser = value;
    this.buildForm();
  }

  get user(): any {
    return this.currentUser;
  }

  @Input() showTemporaryOption = false;

  form!: UntypedFormGroup;
  submitted = false;
  userLabel = '';
  private readonly passwordRegex = '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[^A-Za-z\\d]).{8,}$';

  constructor(
    public activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private usersService: UsersService,
    private spinner: NgxSpinnerService,
    private notifier: NotifierService,
  ) { }

  ngOnInit(): void {
    if (!this.form) {
      this.buildForm();
    }
  }

  private buildForm(): void {
    this.userLabel = this.currentUser?.preferred_username || this.currentUser?.username || this.currentUser?.user_keycloak || '';
    this.form = this.fb.group(
      {
        keycloak_user_id: [this.currentUser?.sub || this.currentUser?.keycloak_user_id || this.currentUser?.id || ''],
        username: [this.userLabel, [Validators.required]],
        password: ['', [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(this.passwordRegex),
        ]],
        confirmPassword: ['', [Validators.required]],
        temporary: [false],
      },
      { validators: this.passwordsMatchValidator },
    );
  }

  close(): void {
    this.activeModal.dismiss('Cancel click');
  }

  async save(): Promise<void> {
    this.submitted = true;

    if (this.form.invalid) {
      return;
    }

    const formValue = this.form.value;

    if (!formValue.keycloak_user_id && !formValue.username) {
      this.notifier.notify('warning', 'No se encontró el usuario para actualizar la contraseña.');
      return;
    }

    const payload: any = {
      password: formValue.password,
      temporary: false,
    };

    if (formValue.keycloak_user_id) {
      payload.keycloak_user_id = formValue.keycloak_user_id;
    } else {
      payload.username = formValue.username;
    }

    this.spinner.show();
    try {
      const response = await firstValueFrom(this.usersService.patchKeycloakExecutiveUserPassword(payload));
      this.notifier.notify('success', response?.message || 'Contraseña actualizada correctamente');
      this.activeModal.close(response);
    } catch (error) {
      this.notifier.notify('error', this.getBackendErrorMessage(error, 'No fue posible actualizar la contraseña.'));
    } finally {
      this.spinner.hide();
    }
  }

  private passwordsMatchValidator(control: AbstractControl): { passwordMismatch: boolean } | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (!password || !confirmPassword) {
      return null;
    }

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  private getBackendErrorMessage(error: any, fallback: string): string {
    const detail = error?.error?.detail;

    if (Array.isArray(detail)) {
      return detail.join(', ');
    }

    if (typeof detail === 'string') {
      return detail;
    }

    return error?.error?.message || error?.message || fallback;
  }
}
